import { EventEmitter, Injectable } from "@angular/core";

import { CHDevice } from "../model";
import { FormGroup } from "@angular/forms";

export type FieldMetadata = SliderMetadata | SlideToggleMetadata | SelectMetadata | CheckboxMetadata; // Add other types

export interface IDeviceInfo {
    accessoryTypes?: string[];
    config?: { fields?: any[]; authType: string; authUrl?: string };
    configurableProperties?: ConfigurableProperty[];
    configProperties?: DeviceClassMetadata[];
    homeDeviceProperties?: Record<string, any>;
    eventPropInfo?: Record<string, unknown>;
    isUnconfigured?: boolean;
    manufacturer?: string;
    model?: string;
    sku?: string;
    type?: string;
    uninstallHelpText?: string;
    discoveryId?: string;
    totalPorts?: number;
}

/* eslint-disable @typescript-eslint/naming-convention */
interface IMetadata {
    unit: string;
    parameterNum: string;
    parameterBytes: string;
    friendlyName: string;
    description: string;
    inputName?: string;
    type?: string;
    IN1?: any;
    IN2?: any;
    IN3?: any;
    IN4?: any;
    IN5?: any;
    IN6?: any;
}

export interface ConfigurableProperty {
    Name: string;
    Arguments?: number;
    Metadata: FieldMetadata;
    DevicePropertyName?: string;
    Value?: any;
}

export interface DeviceClassMetadata {
    Name: string;
    DeviceClass: string;
    Operations: ConfigurableProperty[];
    Properties: ConfigurableProperty[];
}

interface IDeviceMetadataNodeData {
    info: IDeviceInfo;
    path?: Array<{ Properties: string[] } | string>;
}

export interface DeviceInstallMetadata {
    id?: string;
    helpText?: string;
    model: string;
    manufacturer: string;
    driverName?: string;
    ProtocolAdapterName?: string;
    addMethod?: string;
    template?: Record<string, any>;
    verifySuccessfulAdd?: Record<any, any>[];
    requiredProperties?: Record<any, any>;
}

export interface IDeviceMetadataNode {
    see_also?: string[];
    node_data: IDeviceMetadataNodeData;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface SliderMetadata extends IMetadata {
    min: number;
    max: number;
    step: number;
    signed?: number | boolean;
}

export interface CheckboxMetadata extends IMetadata {}

export interface SlideToggleMetadata extends IMetadata {
    enum: any[];
}

export interface SelectMetadata extends IMetadata {
    enum: any[];
}

export interface GenericInputGetterEvent {
    form?: FormGroup;
    metadata: any;
    throttleCall: boolean;
    emitter?: EventEmitter<any>;
    error?: string;
}

interface DeepMergeOptions {
    returnCopy: boolean;
    /**
     * If "deepMerge", then items from each array are paired by index will be recursively deep-merged.
     *  If one array is longer than the other, unpaired items are appended to the result as-is
     */
    arrayMergeStrategy: "deepMerge";
}

@Injectable({
    providedIn: "root",
})
export class DeviceMetadataParser {
    public static installHelpKey = "clarehome_device_install_help";
    public static identifierKey = "clarehome_device_identifier";
    public static nodeDataKey = "node_data";

    public static areAllBooleanValues(values) {
        return values.every(value => typeof value === "boolean" || value === 0 || value === 1 || value === "true" || value === "false");
    }

    public static isSelect(meta: FieldMetadata): meta is SelectMetadata {
        return ((meta || {}) as SelectMetadata).enum !== undefined && !this.isSlideToggle(meta);
    }

    public static isSlider(meta: FieldMetadata): meta is SliderMetadata {
        return ((meta || {}) as SliderMetadata).min !== undefined;
    }

    /**
     * If it has an enum element, of size 2, all boolean | 1 | 0
     *
     * @param meta
     */
    public static isLabeledToggle(meta: FieldMetadata): meta is SlideToggleMetadata {
        const enumVals = ((meta || {}) as SlideToggleMetadata).enum;
        if (enumVals !== undefined) {
            const enumKeys = Object.keys(enumVals);
            if (enumKeys.length === 2 && this.areAllBooleanValues(Object.values(enumVals))) {
                return enumKeys.every(
                    key => key !== "On" && key !== "Off" && key !== "Enabled" && key !== "Disabled" && key !== "Yes" && key !== "No"
                );
            }
        }

        return false;
    }

    /**
     * If it has an enum element, of size 2, all boolean | 1 | 0
     *
     * @param meta
     */
    public static isSlideToggle(meta: FieldMetadata): meta is SlideToggleMetadata {
        const enumVals = ((meta || {}) as SlideToggleMetadata).enum;
        if (enumVals !== undefined) {
            const enumKeys = Object.keys(enumVals);
            if (enumKeys.length === 2) {
                return this.areAllBooleanValues(Object.values(enumVals));
            }
        }

        return false;
    }

    /**
     * If it is a boolean array, make it a checkmark
     *
     * @param meta
     */
    public static isCheckbox(meta: FieldMetadata): meta is CheckboxMetadata {
        if (meta.type === "boolean[]") {
            return true;
        }

        return false;
    }

    public static getInputs(operationOrPropertyData, takeHardcodedInputValues = false) {
        const operation = operationOrPropertyData;
        if (operation.Arguments) {
            const inputs = [];
            // it's an operation
            const metaKeys = Object.keys(operation.Metadata).sort((a, b) => a.localeCompare(b));
            metaKeys.forEach(key => {
                if (key.indexOf("IN") === 0) {
                    const isHardcodedInputValue = typeof operation.Metadata[key] === "string";
                    if (operation.Metadata[key].type === "object") {
                        const metaInputs = operation.Metadata[key].inputs;
                        let metaInputKeys = Object.keys(metaInputs);

                        // If it's a username and password, reverse the order if backwards
                        if (metaInputKeys[0] === "password" && metaInputKeys[1] === "username") {
                            metaInputKeys = ["username", "password"];
                        }

                        metaInputKeys.forEach(input => {
                            operation.Metadata[key].inputs[input].inputName = input;
                            inputs.push(operation.Metadata[key].inputs[input]);
                        });
                    } else if (!isHardcodedInputValue) {
                        operation.Metadata[key].inputName = key;
                        inputs.push(operation.Metadata[key]);
                    } else if (takeHardcodedInputValues) {
                        inputs.push({
                            inputName: key,
                            value: operation.Metadata[key],
                        });
                    }
                }
            });

            return inputs;
        }

        if (operationOrPropertyData.property) {
            // it's a property
            return operationOrPropertyData.property.Metadata;
        }

        if (!Array.isArray(operationOrPropertyData.Metadata)) {
            operationOrPropertyData.inputName = operationOrPropertyData.Name;
            return [operationOrPropertyData.Metadata];
        }

        // it's unaltered metadata
        return operationOrPropertyData.Metadata;
    }

    public static compileFormValue(newValue, metadata) {
        let returnValue;
        if (metadata.setOperation.Metadata.IN1.type === "object") {
            returnValue = { IN1: newValue };
        } else {
            returnValue = newValue;
        }
        return returnValue;
    }

    private static addToDeviceInfo(deviceInfo, node): void {
        if (node[this.nodeDataKey] && node[this.nodeDataKey].info) {
            Object.assign(deviceInfo, node[this.nodeDataKey].info);
        }
    }

    static deepMergeObject<T, S>(targetObject = {}, sourceObject = {}, options: Partial<DeepMergeOptions> = {}) {
        const { returnCopy = true, arrayMergeStrategy = "deepMerge" } = options;
        const recursionOpts = { ...options, returnCopy: false };

        if (returnCopy) {
            targetObject = JSON.parse(JSON.stringify(targetObject));
        }

        Object.keys(sourceObject).forEach(key => {
            const tgt = targetObject[key];
            const src = sourceObject[key];

            if (tgt === undefined) {
                // Nothing to merge
                targetObject[key] = src;
            } else if (Array.isArray(src) || Array.isArray(tgt)) {
                // Merge arrays

                // Assert both src and tgt are arrays
                const isBothArray = Array.isArray(src) && Array.isArray(tgt);
                if (!isBothArray) {
                    console.error("Attempted to merge array with non-array", src, tgt);
                    throw new Error("deepMerge() error");
                }

                if (arrayMergeStrategy === "deepMerge") {
                    // Get indices where both arrays have items defined
                    const minLength = Math.min(tgt.length, src.length);
                    const pairIndices = [...Array(minLength).keys()];

                    // Deep merge the paired items
                    let result = pairIndices.map(idx => {
                        if (typeof src[idx] === "object") {
                            return this.deepMergeObject(tgt[idx], src[idx], recursionOpts);
                        }

                        return src[idx];
                    });

                    // Append the unpaired items, one or both of these slices should be empty
                    result = result.concat(tgt.slice(minLength), src.slice(minLength));

                    targetObject[key] = result;
                }
            } else if (typeof src === "object") {
                // Merge objects
                targetObject[key] = this.deepMergeObject(tgt, src, recursionOpts);
            } else {
                if (typeof src !== typeof tgt) {
                    console.error(`typeof tgt=${tgt} differs from typeof src=${src}`);
                    throw new Error("deepMerge() error");
                }

                // Merge primitives (string, number, etc)
                targetObject[key] = src;
            }
        });

        return targetObject;
    }

    private static canContinueCheckingDeviceProps(
        info: IDeviceMetadataNode,
        path: { Properties: string[] },
        deviceProps,
        deviceInfo
    ): boolean {
        for (const property in path) {
            // should check if deviceProps is truthy and has property before
            // going with theDevice
            if (path.hasOwnProperty(property)) {
                if (!deviceProps.hasOwnProperty(property)) {
                    // object is missing a property
                    return false;
                }

                this.setDeviceInfo(info, path[property], deviceProps[property], deviceInfo);
            }
        }

        return true;
    }

    private static getWildcardProperties(metadata) {
        const wildcards = [];

        if (metadata) {
            const keys = Object.keys(metadata);
            const wildcardKeys: string[] = keys.filter(key => key.includes("*"));

            for (const key of wildcardKeys) {
                const [searchString] = key.split("*");
                wildcards.push({ searchString, key });
            }
        }

        return wildcards;
    }

    private static setDeviceInfo(info: IDeviceMetadataNode, path: Array<{ Properties: string[] } | string>, deviceProps, deviceInfo = {}) {
        let infoPath: IDeviceMetadataNode = info;
        this.addToDeviceInfo(deviceInfo, info);

        const wildcards = this.getWildcardProperties(info);

        if (path) {
            for (const thisPath of path) {
                // may need to return out if path is string and not a prop on the device
                if (typeof thisPath === "string") {
                    if (wildcards) {
                        for (const wildcard of wildcards) {
                            if (deviceProps[thisPath].indexOf(wildcard.searchString) === 0) {
                                this.addToDeviceInfo(deviceInfo, info[wildcard.key]);
                            }
                        }
                    }

                    if (infoPath[deviceProps[thisPath]]) {
                        // it's this redefining of infoPath outside the loop that dives into the nested JSON
                        infoPath = infoPath[deviceProps[thisPath]];

                        if (infoPath.see_also) {
                            for (const seeAlsoPath of infoPath.see_also) {
                                this.addToDeviceInfo(deviceInfo, info[seeAlsoPath]);
                            }
                        }

                        if (infoPath[this.nodeDataKey]) {
                            this.setDeviceInfo(infoPath, infoPath[this.nodeDataKey].path, deviceProps, deviceInfo);
                        }
                    }
                } else if (typeof thisPath !== "string" && !this.canContinueCheckingDeviceProps(info, thisPath, deviceProps, deviceInfo)) {
                    break;
                }
            }
        }

        return deviceInfo;
    }

    public static setPathToDevicePropertyInfo(metadata, devices = {}) {
        if (metadata) {
            const meta = metadata[this.identifierKey];
            if (meta) {
                for (const uid in devices) {
                    if (devices.hasOwnProperty(uid)) {
                        devices[uid].info = this.setDeviceInfo(meta, meta[this.nodeDataKey].path, devices[uid]);
                    }
                }
            }
        }
    }

    public static setPathToSingleDeviceInfo(metadata, device: CHDevice) {
        if (metadata) {
            const meta = metadata[this.identifierKey];
            if (meta) {
                device.info = this.setDeviceInfo(meta, meta[this.nodeDataKey].path, device);
            }
        }
    }

    /**
     * Extract the eventPropInfo from a device based on eventValue
     *
     * @param device The device to extract the eventPropInfoFrom
     * @param eventValue The event value that we are trying to match for extraction
     * @description When looking up an eventable action in device metadata, unless you have the
     * controlPointUid (waterV2, motionV2, etc) and the type of event (Tripped, Opened, etc)
     * the only way to look up the event info is by value comparison.
     *
     * This method iterates over device.info.eventPropInfo (which is a value extracted from
     * device metadata and assigned to this device property) and looks at each controlPointUid (cpName),
     * then each event possible on that control point (eventType). Then the value: ["value", "type"] is
     * converted to a string and compared with eventValue's contatenated string value.
     *
     * If we find a value, we break out of the find method and return the value
     */
    public static getMatchingEventPropInfo(device, eventValue): { cpName: string; eventType: string } {
        let propInfo = { cpName: null, eventType: null };
        if (device?.info?.eventPropInfo) {
            device.info.eventPropInfo.find(event => {
                const events = event.events;
                for (const cpName in events) {
                    if (events?.[cpName]?.Metadata?.enum) {
                        for (const eventType in events[cpName].Metadata.enum) {
                            if (
                                eventValue &&
                                events[cpName].Metadata.enum[eventType] &&
                                JSON.stringify(eventValue) === JSON.stringify(events[cpName].Metadata.enum[eventType])
                            ) {
                                propInfo = { cpName, eventType };
                                return;
                            }
                        }
                    }
                }
            });
        }

        return propInfo;
    }
}
